import { Fragment } from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { N900, P200, P50, P500 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { AiUnionIcon } from "@/templates/confluence-software-page/src/icons/ai-union-icon";

const lozengeStyle = css`
    display: flex;
    align-items: center;
    gap: 3px;
    background-color: ${P50};
    color: ${P500};
    border-radius: 3px;
    padding: 0 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.3px;
    line-height: 1rem;
    text-transform: uppercase;
`;

const lozengeIcon = css`
    color: ${P200};
`;

const confluenceHeroSectionStyle = css`
    display: flex;
    max-width: 1200px;
    flex-direction: column;
    margin: 0 auto ${token("space.600")} auto;
    align-items: center;
    padding-top: ${token("space.800")};
`;

const heroSectionTextStyle = css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: ${token("space.150")} ${token("space.200")} 0 ${token("space.200")};
    max-width: 768px;

    .confluenceHero h1 {
        color: #253858;
        font-weight: 500;
        margin: ${token("space.0")} ${token("space.0")} ${token("space.200")}
            ${token("space.0")};
        text-align: center;

        @media (max-width: 47.99rem) {
            font-size: 2.5rem;
            line-height: 2.875rem;
        }

        @media (min-width: 48rem) and (max-width: 63.99rem) {
            font-size: 2.75rem;
            line-height: 3.125rem;
        }

        @media (min-width: 64rem) {
            font-size: 3rem;
            line-height: 3.5rem;
        }
    }

    .confluenceHero p {
        margin: ${token("space.0")} ${token("space.0")} ${token("space.300")}
            ${token("space.0")};
        text-align: center;
        font-weight: 400;
        color: ${N900};

        @media (max-width: 63.99rem) {
            font-size: 1.125rem;
            line-height: 1.625rem;
        }

        @media (min-width: 64rem) {
            font-size: 1.25rem;
            line-height: 1.75rem;
        }
    }

    .confluenceHero .contentBlock-linkFlex {
        gap: 16px;
        justify-content: center;
        margin-bottom: 0;
    }

    .confluenceHero .contentBlock-linkFlex a {
        margin-top: 0;
    }

    .confluenceHero .link-wrapper {
        @media (max-width: 63.99rem) {
            display: none;
        }
    }
`;

const HeroSection = () => {
    const intl = useIntl();
    const getItFreeText = intl.formatMessage({
        id: "ConfluenceSoftwarePageTemplate.heroCTANew",
        defaultMessage: "Get Confluence free"
    });
    const tryDemoText = intl.formatMessage({
        id: "ConfluenceSoftwarePageTemplate.tryDemo",
        defaultMessage: "Try a demo"
    });

    return (
        <Fragment>
            <section>
                <div css={confluenceHeroSectionStyle}>
                    <div css={lozengeStyle}>
                        <span css={lozengeIcon}>
                            <AiUnionIcon size="9px" />
                        </span>
                        <FormattedMessage
                            id="ConfluenceSoftwarePageTemplate.heroLozenge"
                            defaultMessage="powered by ai"
                        />
                    </div>
                    <div css={heroSectionTextStyle}>
                        <ContentBlock
                            headerType="h1"
                            className={"confluenceHero"}
                            headerText={intl.formatMessage({
                                id: "ConfluenceSoftwarePageTemplate.heroHeaderNew",
                                defaultMessage: "Goodbye silos, hello teamwork"
                            })}
                            bodyText={intl.formatMessage({
                                id: "ConfluenceSoftwarePageTemplate.heroBodyNew",
                                defaultMessage:
                                    "Create, organize, and share work with AI by your side. Turn scattered information into a single source of truth."
                            })}
                            ctaProps={{
                                buttonProps: {
                                    text: getItFreeText,
                                    color: "darkblue",
                                    url: useLocalizedHref(
                                        "/software/confluence/free"
                                    ),
                                    className: "confluence-hero-section-cta",
                                    dataEventProps: {
                                        "data-product-key": "confluence",
                                        "data-event-component": "linkButton",
                                        "data-event-container": "heroSection",
                                        "data-event": "clicked",
                                        "data-label": getItFreeText
                                    }
                                },
                                secondaryLinkProps: {
                                    text: tryDemoText,
                                    href: "/confluence-sandbox-editor",
                                    type: "basic-underline",
                                    className: "confluence-hero-section-editor",
                                    doesArrowAnimate: true,
                                    dataEventProps: {
                                        "data-event-component": "linkButton",
                                        "data-event-container": "heroSection",
                                        "data-event": "clicked",
                                        "data-product-key": "confluence",
                                        "data-label-english": "Try a demo",
                                        "data-label": tryDemoText
                                    }
                                }
                            }}
                        />
                    </div>
                </div>
            </section>
        </Fragment>
    );
};

export default HeroSection;
